import React from 'react';
import { useSelector } from 'react-redux';

import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { leaveGroupClick } from '@wix/bi-logger-groups/v2';

import { selectDialog, selectGroup } from 'store/selectors';

import { useController } from 'common/context/controller';

import { Button } from 'wui/Button';
import { AlertDialog, type IAlertDialogProps } from 'wui/AlertDialog';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogContent } from 'wui/DialogContent';
import { DialogContentText } from 'wui/DialogContentText';
import { DialogActions } from 'wui/DialogActions';

import { GroupMembershipButton } from '../../GroupMembershipButton';

export function LeaveGroupDialog(props: Partial<IAlertDialogProps>) {
  const { t } = useTranslation();
  const bi = useBi();
  const { application$, group$ } = useController();
  const { isMobile } = useEnvironment();

  const dialog = useSelector(selectDialog('leaveGroup'));
  const groupId = dialog.params?.groupId as string;
  const group = useSelector(selectGroup(groupId));

  if (!group) {
    return null;
  }

  return (
    <AlertDialog
      {...props}
      isOpen={dialog.isOpen}
      onClose={handleClose}
      aria-labelledby="lg-dialog-title"
      aria-describedby="lg-dialog-content"
    >
      <DialogTitle
        alert
        id="lg-dialog-title"
        title={t('groups-web.group.actions.leave.group')}
      />
      <DialogContent alert>
        <DialogContentText alert id="lg-dialog-content">
          {t('groups-web.group.actions.leave.confirm', {
            groupName: group.name,
            interpolation: { escapeValue: false },
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions alert>
        <Button
          outlined
          variant="basic"
          onClick={handleClose}
          fullWidth={isMobile}
          data-hook="cancel-button"
        >
          {t('groups-web.cancel')}
        </Button>
        <GroupMembershipButton
          variant="basic"
          groupId={groupId}
          onClick={handleSubmit}
          fullWidth={isMobile}
          data-hook="confirm-button"
        >
          {t('groups-web.group.actions.leave')}
        </GroupMembershipButton>
      </DialogActions>
    </AlertDialog>
  );

  function handleSubmit() {
    group$.leave(groupId);
    bi.report(
      leaveGroupClick({
        group_id: groupId,
        origin: 'leave_dialog_modal',
      }),
    );
  }

  function handleClose() {
    application$.closeDialog('leaveGroup');
  }
}

LeaveGroupDialog.displayName = 'LeaveGroupDialog';
